/**
 * Variantes acessíveis da cor da marca do cliente (runtime).
 *
 * A cor vem de `window.__PM_CONFIG__.primaryColor` e pode ser qualquer coisa (#262362 escuro,
 * um amarelo claro…). CSS ainda não calcula contraste de forma portátil, então derivamos aqui:
 *  - `--pm-brand-on`          texto/ícone SOBRE a marca (branco ou quase-preto, o de maior contraste);
 *  - `--pm-brand-accent-l`    marca usada como TEXTO/ícone no tema claro (≥ 4.5:1 sobre branco);
 *  - `--pm-brand-accent-d`    idem no tema escuro (≥ 4.5:1 sobre a superfície escura);
 *  - `--pm-brand-strong-d`    preenchimento de botão primário no escuro (≥ 3:1 sobre o canvas escuro);
 *  - `--pm-brand-strong-d-on` texto sobre esse preenchimento.
 */

type Rgb = [number, number, number];

const LIGHT_SURFACE: Rgb = [255, 255, 255];
const DARK_SURFACE: Rgb = [0x14, 0x19, 0x22];
const DARK_CANVAS: Rgb = [0x0c, 0x10, 0x17];
const NEAR_BLACK: Rgb = [0x0c, 0x10, 0x17];
const WHITE: Rgb = [255, 255, 255];

/** Converte qualquer cor CSS (hex, rgb(), nome) em RGB usando o canvas do navegador. */
export function parseCssColor(input: string): Rgb | null {
  const s = input.trim();
  const hex = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.replace(/./g, (c) => c + c);
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  if (typeof document === "undefined") return null;
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#000001";
    ctx.fillStyle = s;
    const out = String(ctx.fillStyle);
    if (out === "#000001") return null; // cor inválida
    return parseCssColor(out) ?? parseRgbFn(out);
  } catch {
    return null;
  }
}

function parseRgbFn(s: string): Rgb | null {
  const m = /rgba?\(\s*([\d.]+)[ ,]+([\d.]+)[ ,]+([\d.]+)/i.exec(s);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

function channel(c: number) {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

export function luminance([r, g, b]: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrast(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return [0, 1, 2].map((i) => Math.round(a[i] + (b[i] - a[i]) * t)) as Rgb;
}

export function toHex([r, g, b]: Rgb): string {
  return `#${[r, g, b].map((n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0")).join("")}`;
}

/** Mistura `color` em direção a `toward` até atingir `ratio` contra `bg` (passos de 4%). */
function ensureContrast(color: Rgb, bg: Rgb, ratio: number, toward: Rgb): Rgb {
  let c = color;
  for (let t = 0; t <= 1.0001 && contrast(c, bg) < ratio; t += 0.04) c = mix(color, toward, t);
  return c;
}

function bestOn(bg: Rgb): Rgb {
  // Preferimos branco quando atinge AA (padrão visual das marcas escuras).
  return contrast(WHITE, bg) >= 4.5 ? WHITE : contrast(NEAR_BLACK, bg) >= contrast(WHITE, bg) ? NEAR_BLACK : WHITE;
}

export type BrandPalette = {
  brand: string;
  on: string;
  accentLight: string;
  accentDark: string;
  strongDark: string;
  strongDarkOn: string;
  /** Contrastes resultantes (exibidos na vitrine /design). */
  ratios: { onBrand: number; accentLight: number; accentDark: number; strongDarkVsCanvas: number };
};

export function deriveBrandPalette(input: string): BrandPalette | null {
  const brand = parseCssColor(input);
  if (!brand) return null;
  const on = bestOn(brand);
  const accentLight = ensureContrast(brand, LIGHT_SURFACE, 4.5, NEAR_BLACK);
  const accentDark = ensureContrast(brand, DARK_SURFACE, 4.5, WHITE);
  const strongDark = ensureContrast(brand, DARK_CANVAS, 3, WHITE);
  const strongDarkOn = bestOn(strongDark);
  return {
    brand: toHex(brand),
    on: toHex(on),
    accentLight: toHex(accentLight),
    accentDark: toHex(accentDark),
    strongDark: toHex(strongDark),
    strongDarkOn: toHex(strongDarkOn),
    ratios: {
      onBrand: contrast(on, brand),
      accentLight: contrast(accentLight, LIGHT_SURFACE),
      accentDark: contrast(accentDark, DARK_SURFACE),
      strongDarkVsCanvas: contrast(strongDark, DARK_CANVAS),
    },
  };
}

/** Escreve as variáveis `--pm-brand-*` no elemento (padrão: <html>). */
export function applyBrandPalette(input: string, el: HTMLElement = document.documentElement): BrandPalette | null {
  const p = deriveBrandPalette(input);
  if (!p) return null;
  el.style.setProperty("--color-brand", p.brand);
  el.style.setProperty("--pm-brand-on", p.on);
  el.style.setProperty("--pm-brand-accent-l", p.accentLight);
  el.style.setProperty("--pm-brand-accent-d", p.accentDark);
  el.style.setProperty("--pm-brand-strong-d", p.strongDark);
  el.style.setProperty("--pm-brand-strong-d-on", p.strongDarkOn);
  return p;
}
